import { z } from 'zod';

import {
  LURE_AI_PRIVACY_LINK,
  LURE_AI_SUPPORT_EMAIL,
  LURE_AI_TERMS_LINK,
  LURE_AI_WEB_APP_URL,
  LURE_APP_PRIVACY_LINK,
  LURE_APP_SUPPORT_EMAIL,
  LURE_APP_TERMS_LINK,
  LURE_APP_WEB_APP_URL,
  PHEON_SUPPORT_EMAIL,
  PRIVACY_LINK,
  TERMS_LINK,
  WEB_APP_URL,
} from '@/constants';
import { LAYOUT_NOTES, PAYWALL_NOTES } from '@/constants/campaign-notes';
import { getRandomNumber } from '@/utils/getRandomNumber';

import { CAMPAIGNS_SETUP } from '../CAMPAIGNS_SETUP';
import {
  REGISTRATION_PLATFORM,
  RegistrationPlatformType,
  WebCampaignKeyType,
  WebCampaignType,
} from '../global';

import { queryParamsModule } from './queryParams';
import { WebSubscriptionPlanType } from './subscriptions';

const CampaignVariantSchema = z.enum(['ab_test']);
const AbTestCampaignSchema = z.array(z.string());
const CampaignNameSchema = z.string().min(1);

class WebCampaign {
  private key: WebCampaignKeyType = 'MyCkd1';
  private defaultId: WebCampaignKeyType = 'MyCkd1';
  private _name: string | null = null;
  private _setup: WebCampaignType = CAMPAIGNS_SETUP[this.key];
  private _aiType: WebCampaignType['ai_type'] = 'girl';

  public QUERY_PARAM_KEY = {
    C_VARIANT: 'c_variant',
    C_NAME: 'c_name',
    C_IDS: 'c_ids',
    WEB_CAMPAIGN: 'web_campaign',
  };

  init() {
    this.key = this.retrieveKey();
    this._aiType = this.retrieveAiType();
    this._setup = CAMPAIGNS_SETUP[this.key];
  }

  get aiGender() {
    return this._setup.gender_ai_type;
  }

  get setup() {
    return this._setup;
  }

  get name() {
    return this._name;
  }

  set name(name: string | null) {
    this._name = name;
  }

  getKey() {
    return this.key;
  }

  retrieveKey(): WebCampaignKeyType {
    const urlParams = new URLSearchParams(window.location.search);

    if (urlParams.has(this.QUERY_PARAM_KEY.C_VARIANT)) {
      const campaignNameSchema = CampaignNameSchema.safeParse(
        urlParams.get(this.QUERY_PARAM_KEY.C_NAME),
      );
      const campaignVariantParse = CampaignVariantSchema.safeParse(
        urlParams.get(this.QUERY_PARAM_KEY.C_VARIANT),
      );
      const abTestCampaignIdsParse = AbTestCampaignSchema.safeParse(
        urlParams.getAll(this.QUERY_PARAM_KEY.C_IDS),
      );

      if (
        campaignVariantParse.success &&
        abTestCampaignIdsParse.success &&
        campaignNameSchema.success
      ) {
        this.name = campaignNameSchema.data;

        const areAllIdsValid = abTestCampaignIdsParse.data.every(
          (id) => id in CAMPAIGNS_SETUP,
        );

        if (areAllIdsValid) {
          const randomIdIdx = getRandomNumber(
            0,
            abTestCampaignIdsParse.data.length - 1,
          );

          return abTestCampaignIdsParse.data[randomIdIdx] as WebCampaignKeyType;
        }
      }
    }

    const key = urlParams.get(this.QUERY_PARAM_KEY.WEB_CAMPAIGN);

    if (key && key in CAMPAIGNS_SETUP) {
      return key as WebCampaignKeyType;
    }

    return this.defaultId;
  }

  get aiType() {
    return this._aiType;
  }

  private retrieveAiType = (): WebCampaignType['ai_type'] => {
    return CAMPAIGNS_SETUP[this.key].ai_type;
  };

  getLayoutNotes() {
    return CAMPAIGNS_SETUP[this.getKey()].layoutNotes;
  }

  /**
   * For campaigns with plansByGender (e.g. g34bjedMGrp_aio): returns default plan
   * for the selected gender. Pass gender from user?.onboarding_data?.gender (AI preference
   * on multigender). For g34bjedMGrp_aio_G, pass identify from onboarding_data.identify.
   */
  getDefaulPlan(
    selectedGender?: 'male' | 'female' | null,
    userIdentify?: string | null,
  ): WebSubscriptionPlanType {
    const setup = this._getPlansSetup(selectedGender, userIdentify);
    return setup.defaultPlan;
  }

  /**
   * For campaigns with plansByGender (e.g. g34bjedMGrp_aio): returns plans for the
   * selected gender. Pass gender from user?.onboarding_data?.gender.
   */
  getPlans(
    selectedGender?: 'male' | 'female' | null,
    userIdentify?: string | null,
  ): WebSubscriptionPlanType[] {
    const setup = this._getPlansSetup(selectedGender, userIdentify);
    return setup.plans;
  }

  private _plansGenderKey(
    aiPreference?: 'male' | 'female' | null,
    userIdentify?: string | null,
  ): 'male' | 'female' | null | undefined {
    if (
      this.key === 'g34bjedMGrp_aio_G' &&
      aiPreference === 'male' &&
      userIdentify === 'female'
    ) {
      return 'female';
    }
    return aiPreference ?? undefined;
  }

  private _getPlansSetup(
    selectedGender?: 'male' | 'female' | null,
    userIdentify?: string | null,
  ): WebCampaignType {
    const byGender = this._setup.plansByGender;
    const genderKey = this._plansGenderKey(selectedGender, userIdentify);
    if (byGender && genderKey) {
      return {
        ...this._setup,
        plans: byGender[genderKey] ?? byGender.male,
      };
    }
    return this._setup;
  }

  getCoins(): string[] {
    return this._setup.coins;
  }

  isAnimated() {
    return this._setup.animated;
  }

  canLogFBEvents() {
    return this._setup.canLogFBEvents;
  }

  isAxonTrackingEnabled() {
    return this._setup.enableAxonTracking ?? false;
  }

  shouldRejectPrepaidCards() {
    return this._setup.rejectPrepaidCards ?? false;
  }

  getPrepaidCardRejectionMessage() {
    if (!this.shouldRejectPrepaidCards()) {
      return null;
    }

    // Return different messages based on campaign ID
    switch (this.key) {
      case 'g34bjedMGPPC1':
        return 'Prepaid cards are not accepted';
      case 'g34bjedMGPPC2':
        return 'We are unable to process your card, please try another card';
      default:
        return null; // no message means no rejection
    }
  }

  getStaticTheme() {
    if (this._setup.staticTheme === 'dark-violet') {
      return 'dark-violet';
    }
    if (this._setup.staticTheme === 'domination') {
      return 'domination';
    }
    if (this._setup.staticTheme === 'purple-gradient') {
      return 'purple-gradient';
    }
  }

  isCampaignPaymentless() {
    return this._setup.paywall === null;
  }

  /**
   * Returns the platform value to use for user registration.
   * Uses registrationPlatform from campaign setup if specified, otherwise defaults to 'web'.
   */
  getRegistrationPlatform(): RegistrationPlatformType {
    return this._setup.registrationPlatform ?? REGISTRATION_PLATFORM.WEB;
  }

  removeServiceQueryParams() {
    queryParamsModule.removeParam(this.QUERY_PARAM_KEY.C_IDS);
    queryParamsModule.removeParam(this.QUERY_PARAM_KEY.C_VARIANT);
    queryParamsModule.removeParam(this.QUERY_PARAM_KEY.C_NAME);
  }

  isLayoutLureAI() {
    return this.getLayoutNotes().includes(LAYOUT_NOTES.LURE_AI_BRANDING);
  }

  isLayoutLureApp() {
    return this.getLayoutNotes().includes(LAYOUT_NOTES.LURE_APP_BRANDING);
  }

  isLayoutLure() {
    return this.isLayoutLureAI() || this.isLayoutLureApp();
  }

  isPaywallLureAI() {
    return this.setup.paywallNotes.includes(PAYWALL_NOTES.LURE_AI_BRANDING);
  }

  isPaywallLureApp() {
    return this.setup.paywallNotes.includes(PAYWALL_NOTES.LURE_APP_BRANDING);
  }

  isPaywallLure() {
    return this.isPaywallLureAI() || this.isPaywallLureApp();
  }

  isLure() {
    return this.isLayoutLure() || this.isPaywallLure();
  }

  getLegalSupportEmail = () => {
    if (this.isLayoutLureAI() || this.isPaywallLureAI()) {
      return LURE_AI_SUPPORT_EMAIL;
    }
    if (this.isLayoutLureApp() || this.isPaywallLureApp()) {
      return LURE_APP_SUPPORT_EMAIL;
    }

    return PHEON_SUPPORT_EMAIL;
  };

  getAuthUrl = (authToken: string) => {
    if (this.isPaywallLureAI()) {
      return `${LURE_AI_WEB_APP_URL}/auth/${authToken}`;
    }

    if (this.isPaywallLureApp()) {
      return `${LURE_APP_WEB_APP_URL}/auth/${authToken}`;
    }

    return `${WEB_APP_URL}/auth/${authToken}`;
  };

  getLegalPrivacyLink = () => {
    const layoutNotes = webCampaign.getLayoutNotes();

    if (layoutNotes.includes(LAYOUT_NOTES.LURE_AI_BRANDING)) {
      return LURE_AI_PRIVACY_LINK;
    }
    if (layoutNotes.includes(LAYOUT_NOTES.LURE_APP_BRANDING)) {
      return LURE_APP_PRIVACY_LINK;
    }
    return PRIVACY_LINK;
  };

  getLegalTermsLink = () => {
    const layoutNotes = webCampaign.getLayoutNotes();

    if (layoutNotes.includes(LAYOUT_NOTES.LURE_AI_BRANDING)) {
      return LURE_AI_TERMS_LINK;
    }
    if (layoutNotes.includes(LAYOUT_NOTES.LURE_APP_BRANDING)) {
      return LURE_APP_TERMS_LINK;
    }
    return TERMS_LINK;
  };
}

export const webCampaign = new WebCampaign();
